import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';
import { useNavigate } from 'react-router-dom';


function JTF() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container' >
            <div>
                <Breadcrumb route="/case-studies" pageTitle="John Templeton Foundation" />
            </div>
            <section className='article-row-1' id='jtf-background'>
                <p>Philanthropy</p>
                <h2>John Templeton Foundation</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: West Conshohocken, Pennsylvania 🇺🇲</span>
                    </div>
                </div>
            </section>


            <section className='article-intro-section'>
                <h1>How We Improved article's Grant Classification and Trend Analysis Using Artificial Intelligence</h1>
                <p>
                    John Templeton Foundation is a philanthropic organization that funds interdisciplinary research. Their mission is to start awe-inspiring conversations. They strive for a world where people are curious about the wonders of the universe and seek to live purposeful lives.
                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>The John Templeton Foundation (article) struggled to efficiently analyze and classify grant applications and academic publications. Challenges that hindered its ability to scale operations, maintain data integrity, and extract actionable insights.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We customized OpenAlex's text classification tool to fit JTF's specific requirements. We added relevant topics, fine-tuned the classification model, and enabled API call functionality. We then ensured accuracy and reliability through rigorous testing.</p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>The enhanced classification system enabled JTF to streamline its grant application process, produce more informative and actionable results, and identify trends across supported research and publications.</p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>Our Client's Situation:</h3>
                <p>
                    JTF is one of the few foundations their size to allow people to submit their ideas to them on any topic. However, they also have prioritized, strategic areas they focus on.
                    Given that the vast majority of JTF's grants are used to fund academic research, the John Templeton Foundation needed a strong and efficient mechanism to analyze and classify the numerous grant applications and academic publications they receive.
                    Manual processes were cumbersome and inconsistent, existing proprietary meta databases were inadequate and expensive, and Machine Learning and Neuro Linguistic Programming required lots of data and excellent training sets.
                    This translated into a set of potential challenges:
                </p>
                <ul>
                    <li>Inefficiency in operations due to manual application processing</li>
                    <li>Evaluation inconsistency and quality variability</li>
                    <li>An inadequate utilization of data, with limited insights and data silos</li>
                    <li>Issues with scalability due to the unsustainability of manual processes</li>
                    <li>High costs of using proprietary meta-databases</li>
                    <li>Data integrity and security risks in case of manual processing</li>
                </ul>
                <p>
                    Hadn't they promptly found a solution, they would have faced the end of life for the Relius product, they would have been unable to use the new version, and they would have lost access to support, too.
                </p>
            </section>

            <section className='article-section-3'>
                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    We started off by revamping the John Templeton Foundation's website, creating a seamless and dynamic user experience with CSS transitions, parallax scrolling, and intelligent page indicators, all while ensuring a modern and tech-focused identity through accessible and technology-centric images, and rounded corners for a contemporary touch.
                </p>
                <p>
                    Our team personalized the experience by incorporating valuable tools like research trackers, grant maximizers, and data visualization tools. We integrated comparisons from various research sources and product bundles with flexibility for future use, offering users a comprehensive range of options.
                </p>
                <p>
                    We then implemented Magnolia CMS capabilities, enabling rules for diverse copy and image delivery across the site. The website's personalization features recognized users, emphasizing unowned products and highlighting new tools tailored to individual needs.
                </p>
                <p>
                    We also ensured that the content management system supported infographics, videos, blogging formats, and integrated social media content.
                </p>
                <p>
                    Finally, we wrapped the architecture behind the user portal with a contemporary UX/UI design.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>UX/UI design and front-end development.</li>
                    <li>The incorporation of new functionalities and tools.</li>
                    <li>Customization and optimized website responsiveness.</li>
                    <li>We ensured that the content management system would support infographics, videos, blogging formats, and integrated social media content.</li>
                </ul>

                <h3>The Results</h3>
                <ul>
                    <li>Our team leveraged its expertise in designing and developing marketing websites that not only instantly communicate capabilities but also empower organizations through streamlined content updates, eliminating coordination hassles, and expediting deployment processes.</li>
                    <li>We ensured an inclusive and visitor-friendly digital experience, enabling the website to seamlessly display across various screen devices in adherence to WCAG standards.</li>
                    <li>We built a clear and concise homepage, showcasing research and grant overviews with market-leading insights, minimum requirements, and transparent information. The page includes intuitive CTAs for easy access, interactive tools, security information, and contact details to talk to a specialist.</li>
                </ul>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    We delivered an elevated and seamless experience to enhance our client's customers' financial journeys.
                    The John Templeton Foundation could now leverage its site to appropriately showcase its insights and unique services and come
                    across as a secure and dependable philanthropic organization to its users. We helped streamline
                    their content updates, ensured accessibility compliance, and accelerated deployment to help them stand out,
                    offer personalized guidance, and deliver an exceptional online experience to their users.
                </p>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>

            <section className='ourwork-row-14 blog-row-14'>
                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>


            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}

export default JTF;